import {
  FddmPreferenceValues,
  PreferencesState,
  StoredFddmPreferenceValues,
  getDefaultFddmPreference,
} from '@/modules/floorplanDesign/preferences';
import { FloorplanRevision } from '@thive/fddm-types';

export type PreferencesActions = {
  setPreferences(preferences: PreferencesState): void;
  resetPreferences(): void;
  deletePreference<PreferenceKey extends keyof FddmPreferenceValues>(key: PreferenceKey): void;
  getPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
  ): FddmPreferenceValues[PreferenceKey];
  setPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
    value: FddmPreferenceValues[PreferenceKey],
  ): void;
};

type PreferencesSliceState = {
  uxonly: FloorplanRevision['uxonly'];
};

type PreferencesAction = string | { type: string };

type PreferencesSet<TState extends PreferencesSliceState> = <A extends PreferencesAction>(
  partial: Partial<PreferencesSliceState> | ((state: TState) => Partial<PreferencesSliceState>),
  replace?: boolean,
  action?: A,
) => void;

type PreferencesGet<TState extends PreferencesSliceState> = () => TState;

const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'number');

const matchesStoredPreferenceType = <PreferenceKey extends keyof StoredFddmPreferenceValues>(
  value: unknown,
  fallbackValue: StoredFddmPreferenceValues[PreferenceKey],
): value is StoredFddmPreferenceValues[PreferenceKey] => {
  if (Array.isArray(fallbackValue)) {
    return isNumberArray(value);
  }

  if (typeof fallbackValue === 'string') {
    return typeof value === 'string';
  }

  if (typeof fallbackValue === 'number') {
    return typeof value === 'number';
  }

  if (typeof fallbackValue === 'boolean') {
    return typeof value === 'boolean';
  }

  return false;
};

/**
For backend reasons, the real preferences are inside of additionalProperties. It is what it is...
 */
const readAdditionalProperties = (
  preferences: PreferencesSliceState['uxonly']['preferences'],
): Record<string, unknown> => {
  const { additionalProperties } = preferences;
  if (
    additionalProperties !== null &&
    typeof additionalProperties === 'object' &&
    !Array.isArray(additionalProperties)
  ) {
    const record: Record<string, unknown> = {};
    Object.entries(additionalProperties).forEach(([key, value]) => {
      record[key] = value;
    });
    return record;
  }
  return {};
};

export const createPreferencesSlice = <TState extends PreferencesSliceState>(
  set: PreferencesSet<TState>,
  get: PreferencesGet<TState>,
): PreferencesActions => ({
  setPreferences(preferences) {
    set(
      (state) => ({
        uxonly: {
          ...state.uxonly,
          preferences: {
            additionalProperties: {
              ...preferences,
            },
          },
        },
      }),
      undefined,
      {
        type: 'preferences/set',
      },
    );
  },

  resetPreferences() {
    set(
      (state) => ({
        uxonly: {
          ...state.uxonly,
          preferences: {
            additionalProperties: {},
          },
        },
      }),
      undefined,
      {
        type: 'preferences/reset',
      },
    );
  },

  deletePreference(key) {
    set(
      (state) => {
        const nextAdditionalProperties = readAdditionalProperties(state.uxonly.preferences);
        delete nextAdditionalProperties[key];

        return {
          uxonly: {
            ...state.uxonly,
            preferences: {
              additionalProperties: nextAdditionalProperties,
            },
          },
        };
      },
      undefined,
      {
        type: 'preferences/delete',
      },
    );
  },

  getPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
  ): FddmPreferenceValues[PreferenceKey] {
    const additionalProperties = readAdditionalProperties(get().uxonly.preferences);
    const fallbackValue = getDefaultFddmPreference(key);

    if (!Object.prototype.hasOwnProperty.call(additionalProperties, key)) {
      return fallbackValue;
    }

    const preference = additionalProperties[key];

    if (matchesStoredPreferenceType<PreferenceKey>(preference, fallbackValue)) {
      return preference;
    }

    return fallbackValue;
  },

  setPreference(key, value) {
    set(
      (state) => {
        const additionalProperties = readAdditionalProperties(state.uxonly.preferences);
        return {
          uxonly: {
            ...state.uxonly,
            preferences: {
              additionalProperties: {
                ...additionalProperties,
                [key]: value,
              },
            },
          },
        };
      },
      undefined,
      {
        type: 'preferences/item/set',
      },
    );
  },
});
